// Per-platform "how to enable location" steps, shared by the cash LocationGate
// and the BMO VenuePicker so the two surfaces never drift. Returned as a
// computed that reads navigator lazily — it is only rendered in a
// location_needed state, which is reachable client-side only (the check always
// starts from a click), so SSR never evaluates the getter.
import type { Ref } from 'vue'
import { useNativeBridge } from './useNativeBridge'

export interface LocationHelp { label: string; steps: string[] }

export function useLocationHelp(deniedForever: Ref<boolean>) {
  const bridge = useNativeBridge()
  return computed<LocationHelp>(() => {
    if (bridge.isNative.value) {
      return {
        label: 'Buzztime app',
        steps: deniedForever.value
          ? ['Open your device Settings for the Buzztime app', 'Allow Location "While Using the App"', 'Return here and try again']
          : ['Tap "Try again" and choose Allow when the app asks for your location'],
      }
    }
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
    const isIOS = /iPhone|iPad|iPod/.test(ua)
    const isAndroid = /Android/.test(ua)
    const isFirefox = /Firefox\//.test(ua)
    const isSafari = /Safari\//.test(ua) && !/Chrome|Chromium|Edg\//.test(ua)
    if (isIOS) return {
      label: 'iPhone / iPad',
      steps: ['Open Settings → Privacy & Security → Location Services (turn on)',
              'Scroll to your browser (e.g. Safari Websites) → set "While Using the App"',
              'Reload this page and try again'],
    }
    if (isAndroid) return {
      label: 'Android',
      steps: ['In the browser tap ⋮ → Settings → Site settings → Location → Allowed',
              'If asked, also enable Location in the Android quick settings',
              'Try again'],
    }
    if (isFirefox) return {
      label: 'Firefox',
      steps: ['Click the shield/lock icon next to the address',
              'Clear the blocked Location permission',
              'Try again and choose Allow'],
    }
    if (isSafari) return {
      label: 'Safari',
      steps: ['Safari menu → Settings → Websites → Location',
              'Set this site to Allow', 'Try again'],
    }
    return {
      label: 'Chrome / Edge',
      steps: ['Click the lock icon next to the address → Site settings',
              'Set Location to Allow', 'Reload this page and try again'],
    }
  })
}
